// Erweiterte Cashflow-Planung mit Scroll - src/components/CashflowPlanungScroll.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import CashflowChart from "./CashflowChart";
import BudgetVerwaltung from "./BudgetVerwaltung";
import {
  getBudgetEntries,
  updateBudgetEntryStatus,
  saveBudgetEntry,
  calculateAverageMonthlyRecurringExpenses,
  getCashflowWarnschwelle,
} from "../services/azureBudgetService";
import {
  generateDynamicMwstCashflowEntries,
  calculateAllQuartersMwst,
} from "../services/mwstService";
import WeeklyDetailsTable from "./WeeklyDetailsTable";

import { getBookings } from "../services/azureBookingService";
import { getAllPhases } from "../services/azureProjectPhasesService";
import { formatCurrency } from "../utils/formatUtils";
import {
  calculateCurrentBankSaldo,
  generateExtendedCashflow,
} from "../services/cashflowEngine";

const CashflowPlanung = () => {
  const [allCashflowData, setAllCashflowData] = useState([]);
  const [visibleData, setVisibleData] = useState([]);
  const [startingSaldo, setStartingSaldo] = useState(0);
  const [loading, setLoading] = useState(false);
  const [mwstDetails, setMwstDetails] = useState([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [totalWeeks, setTotalWeeks] = useState(52);
  const [quarterlyMwstSummary, setQuarterlyMwstSummary] = useState([]);
  const [monthlyAverage, setMonthlyAverage] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [filters, setFilters] = useState({ typ: "", name: "", status: "" });
  const [showAddPositionModal, setShowAddPositionModal] = useState(false);

  // Warnschwelle für den Graph: gespeicherter Wert aus der Budget-Verwaltung,
  // sonst Standard = 3 x durchschnittliche regelmässige Monatsausgaben
  const [warnschwelle, setWarnschwelle] = useState(0);

  // Einnahmen, deren Versanddatum in <= 5 Tagen liegt (oder überfällig ist)
  // und die noch nicht als versendet markiert sind -> Erinnerungs-Banner
  const [invoiceReminders, setInvoiceReminders] = useState([]);


  const containerRef = useRef();
  // Phasen-Namen (PhaseId -> Name) fuer die Anzeige von Projekt-Ausgaben;
  // Budget-Eintraege tragen im phase-Feld teils IDs, teils Klartext-Namen
  const phaseNamesRef = useRef({});
  const VISIBLE_WEEKS = 13;
  const PRELOAD_WEEKS = 26;

  useEffect(() => {
    loadInitialData();
  }, []);

  // Einnahmen mit Versanddatum in den nächsten 5 Tagen (oder überfällig),
  // die noch nicht versendet/bezahlt sind — Erinnerung, Rechnungen zu stellen
  const computeInvoiceReminders = (entries) => {
    const limit = new Date();
    limit.setHours(0, 0, 0, 0);
    limit.setDate(limit.getDate() + 5);

    return entries
      .filter(
        (e) =>
          e.typ === "Einnahme" &&
          e.typ_timing === "Einmalig" &&
          e.versand_datum &&
          !e.sent_date &&
          !e.paid_date &&
          e.status !== "sent" &&
          e.status !== "paid" &&
          new Date(e.versand_datum) <= limit
      )
      .sort((a, b) => (a.versand_datum < b.versand_datum ? -1 : 1));
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [budgetEntries, bookings, allPhases, storedWarnschwelle] =
        await Promise.all([
          getBudgetEntries(),
          getBookings(),
          getAllPhases(),
          getCashflowWarnschwelle(),
        ]);

      const phaseMap = {};
      allPhases.forEach((p) => { if (p.id) phaseMap[p.id] = p.name || ""; });
      phaseNamesRef.current = phaseMap;

      // Warnschwelle: fixer Wert aus Budget-Verwaltung oder 3 x Ø-Monatsausgaben
      const avgMonthly = calculateAverageMonthlyRecurringExpenses(budgetEntries);
      setWarnschwelle(
        storedWarnschwelle ?? Math.round(avgMonthly * 3 * 100) / 100
      );

      setInvoiceReminders(computeInvoiceReminders(budgetEntries));

      const currentBankSaldo = calculateCurrentBankSaldo(bookings);
      setStartingSaldo(currentBankSaldo);

      const today = new Date();
      const endDate = new Date(today.getTime() + 52 * 7 * 24 * 60 * 60 * 1000);

      const mwstEntries = await generateDynamicMwstCashflowEntries(
        budgetEntries,
        today,
        endDate
      );


      const initialWeeks = generateExtendedCashflow(
        budgetEntries,
        currentBankSaldo,
        today,
        52,
        mwstEntries,
        phaseNamesRef.current
      );

      const monthlyAvg = calculateMonthlyAverageExpenses(budgetEntries);
      setMonthlyAverage(monthlyAvg);

      setAllCashflowData(initialWeeks);
      setVisibleData(initialWeeks.slice(0, VISIBLE_WEEKS));

      const quarterSummary = await calculateAllQuartersMwst(
        budgetEntries,
        today
      );
      setQuarterlyMwstSummary(quarterSummary);

      setMwstDetails(mwstEntries);
    } catch (error) {
      console.error("Fehler beim Laden der Cashflow-Daten:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthlyAverageExpenses = (budgetEntries) => {
    let yearlyTotal = 0;

    budgetEntries.forEach((entry) => {
      if (entry.typ !== "Ausgabe") return;

      // Ignore 4000 accounts (e.g., 4000, 4100, 4200, etc.)
      const konto = entry.konto?.toString() || "";
      if (konto.startsWith("4")) return;

      // Exclude project costs (subplaner expenses)
      const projekt = entry.projekt?.trim();
      if (projekt) return;

      const betrag = entry.brutto_betrag || entry.betrag || 0;

      switch (entry.typ_timing) {
        case "Regelmässig":
          if (entry.haeufigkeit === "monatlich") {
            yearlyTotal += betrag * 12;
          } else if (entry.haeufigkeit === "quartalsweise") {
            yearlyTotal += betrag * 4;
          } else if (entry.haeufigkeit === "jährlich") {
            yearlyTotal += betrag;
          }
          break;
        case "Einmalig":
          yearlyTotal += betrag;
          break;
      }
    });

    return yearlyTotal / 12;
  };

  const loadMoreWeeks = useCallback(
    async (direction = "forward") => {
      if (loading) return;

      setLoading(true);
      try {
        const budgetEntries = await getBudgetEntries();
        const lastWeek = allCashflowData[allCashflowData.length - 1];
        const additionalWeeks = 26;

        let newWeeks;
        if (direction === "forward") {
          const nextStartDate = new Date(
            lastWeek.weekEnd.getTime() + 24 * 60 * 60 * 1000
          );
          const nextEndDate = new Date(
            nextStartDate.getTime() + additionalWeeks * 7 * 24 * 60 * 60 * 1000
          );

          const additionalMwstEntries =
            await generateDynamicMwstCashflowEntries(
              budgetEntries,
              nextStartDate,
              nextEndDate
            );

          // Die Engine rechnet Salden und Rückstellung selbst weiter (Startwerte der
          // letzten Woche). Früher lief hier eine zweite Kopie der Formel — genau
          // die Drift-Falle, gegen die ADR 0043 die Engine extrahiert hat.
          newWeeks = generateExtendedCashflow(
            budgetEntries,
            lastWeek.kumulativ,
            nextStartDate,
            additionalWeeks,
            additionalMwstEntries,
            phaseNamesRef.current,
            lastWeek.kumulativMitPrognose ?? lastWeek.kumulativ
          );

          newWeeks.forEach((week, index) => {
            week.weekIndex = allCashflowData.length + index;
          });

          setAllCashflowData((prev) => [...prev, ...newWeeks]);
          setTotalWeeks((prev) => prev + additionalWeeks);
          setMwstDetails((prev) => [...prev, ...additionalMwstEntries]);
        }
      } catch (error) {
        console.error("Fehler beim Laden weiterer Wochen:", error);
      } finally {
        setLoading(false);
      }
    },
    [allCashflowData, loading]
  );

  const handleScroll = useCallback(
    (newOffset) => {
      const clampedOffset = Math.max(
        0,
        Math.min(newOffset, Math.max(0, allCashflowData.length - VISIBLE_WEEKS))
      );

      if (clampedOffset !== currentOffset) {
        setCurrentOffset(clampedOffset);

        const newVisibleData = allCashflowData.slice(
          clampedOffset,
          clampedOffset + VISIBLE_WEEKS
        );
        setVisibleData(newVisibleData);

        if (clampedOffset > allCashflowData.length - PRELOAD_WEEKS) {
          loadMoreWeeks("forward");
        }
      }
    },
    [currentOffset, allCashflowData, loadMoreWeeks]
  );

  const handleWeekClick = (weekIndex) => {
    const actualWeekIndex = currentOffset + weekIndex;
    const row = document.querySelector(`[data-week-index="${actualWeekIndex}"]`);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      row.classList.add("week-row-highlight");
      setTimeout(() => row.classList.remove("week-row-highlight"), 2000);
    }
  };

  const handleEntryClick = (entry) => {
    // Öffne Bearbeitungs-Modal für den angeklickten Eintrag
    if (entry.id && !entry.ist_mwst_abrechnung) {
      setSelectedEntryId(entry.id);
      setShowEditModal(true);
    }
  };

  const handleCloseEditModal = async () => {
    setShowEditModal(false);
    setSelectedEntryId(null);
    // Reload data after editing
    await loadInitialData();
  };

  const handleAddPosition = () => {
    setShowAddPositionModal(true);
  };

  const handleCloseAddPositionModal = async () => {
    setShowAddPositionModal(false);
    // Reload data after adding
    await loadInitialData();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleStatusChange = async (entryId, newStatus, entryData = null) => {
    try {
      // Finde den Eintrag in den Wochen, um zu prüfen, ob es wiederkehrend ist
      let targetEntry = null;
      for (const week of allCashflowData) {
        const found = [...week.budgetDetails, ...week.mwstDetails].find(
          (e) => e.id === entryId && e.ist_wiederkehrend
        );
        if (found) {
          targetEntry = found;
          break;
        }
      }

      if (targetEntry && targetEntry.ist_wiederkehrend) {
        // Wiederkehrende Zahlung: Erstelle eine einmalige Kopie für diese Instanz
        const instanceDate = targetEntry.instanceDate || new Date();
        const monthYear = instanceDate.toLocaleDateString("de-CH", {
          month: "long",
          year: "numeric",
        });

        const recurringEntry = targetEntry.recurring_entry;
        const oneTimeCopy = {
          typ: recurringEntry.typ,
          name: `${recurringEntry.name} (${monthYear})`,
          konto: recurringEntry.konto,
          typ_timing: "Einmalig",
          wann: instanceDate.toISOString().split("T")[0],
          betrag: recurringEntry.betrag,
          projekt: recurringEntry.projekt || "",
          phase: recurringEntry.phase || "",
          mwst_satz: recurringEntry.mwst_satz,
          ist_brutto: recurringEntry.ist_brutto,
          ist_prognose: recurringEntry.ist_prognose,
          brutto_betrag: recurringEntry.brutto_betrag,
          netto_betrag: recurringEntry.netto_betrag,
          mwst_betrag: recurringEntry.mwst_betrag,
          status: newStatus,
          created_from_recurring_id: entryId,
          instance_period: monthYear,
        };

        // Füge Status-spezifische Daten hinzu
        if (newStatus === "sent") {
          oneTimeCopy.sent_date = new Date().toISOString().split("T")[0];
        } else if (newStatus === "paid") {
          oneTimeCopy.paid_date = new Date().toISOString().split("T")[0];
        } else if (newStatus === "received") {
          // Für Ausgaben: Rechnung erhalten
          // Kein spezielles Datum-Feld nötig, Status reicht
        }

        // Speichere die einmalige Kopie
        await saveBudgetEntry(oneTimeCopy);
      } else {
        // Einmalige Zahlung: Aktualisiere den Status direkt
        const statusData = {};
        if (newStatus === "sent") {
          statusData.sent_date = new Date().toISOString().split("T")[0];
        } else if (newStatus === "paid") {
          statusData.paid_date = new Date().toISOString().split("T")[0];
        } else if (newStatus === "received") {
          // Für Ausgaben: Rechnung erhalten
          // Kein spezielles Datum-Feld nötig, Status reicht
        }

        await updateBudgetEntryStatus(entryId, newStatus, statusData);
      }

      // Reload data to reflect changes
      await loadInitialData();
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Status:", error);
      alert("Fehler beim Aktualisieren des Status.");
    }
  };

  if (loading && allCashflowData.length === 0) {
    return <div className="cashflow-loading">Lade Cashflow-Daten...</div>;
  }

  return (
    <div className="cashflow-planung-scroll">

      {loading && allCashflowData.length > 0 && (
        <div className="lazy-loading-indicator">Lade weitere Wochen...</div>
      )}

      <div className="cashflow-chart-container-scroll" ref={containerRef}>
        <CashflowChart
          data={visibleData}
          allData={allCashflowData}
          warnschwelle={warnschwelle}
          startingSaldo={startingSaldo}
          onWeekClick={handleWeekClick}
          currentOffset={currentOffset}
          onScroll={handleScroll}
          totalWeeks={allCashflowData.length}
        />
      </div>

      {/* Erinnerung: Rechnungen rechtzeitig verschicken (Versanddatum <= 5 Tage) */}
      {invoiceReminders.length > 0 && (
        <div className="invoice-reminder-banner">
          <div className="invoice-reminder-banner__title">
            Rechnungen verschicken — {invoiceReminders.length}{" "}
            {invoiceReminders.length === 1 ? "Rechnung erreicht" : "Rechnungen erreichen"}{" "}
            in den nächsten 5 Tagen das Versanddatum:
          </div>
          <ul className="invoice-reminder-banner__list">
            {invoiceReminders.map((entry) => {
              const todayStr = new Date().toISOString().split("T")[0];
              const ueberfaellig = entry.versand_datum < todayStr;
              return (
                <li key={entry.id} className="invoice-reminder-banner__item">
                  <span
                    className={`invoice-reminder-banner__datum${
                      ueberfaellig ? " ueberfaellig" : ""
                    }`}
                    title={ueberfaellig ? "Versanddatum überschritten" : undefined}
                  >
                    {new Date(entry.versand_datum).toLocaleDateString("de-CH")}
                  </span>
                  <span
                    className="invoice-reminder-banner__name"
                    onClick={() => handleEntryClick(entry)}
                    title="Eintrag öffnen"
                  >
                    {entry.name}
                  </span>
                  <span className="invoice-reminder-banner__betrag">
                    {formatCurrency(entry.brutto_betrag || entry.betrag)}
                  </span>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleStatusChange(entry.id, "sent")}
                    title="Als versendet markieren"
                  >
                    Versendet
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <WeeklyDetailsTable
        weekData={allCashflowData}
        formatCurrency={formatCurrency}
        monthlyAverage={monthlyAverage}
        onLoadMore={() => loadMoreWeeks("forward")}
        hasMore={allCashflowData.length < 260}
        onEntryClick={handleEntryClick}
        onAddPosition={handleAddPosition}
        filters={filters}
        onFilterChange={handleFilterChange}
        onStatusChange={handleStatusChange}
      />

      {showEditModal && selectedEntryId && (
        <BudgetVerwaltung
          showFormDirectly={true}
          presetData={{ id: selectedEntryId }}
          onClose={handleCloseEditModal}
        />
      )}

      {showAddPositionModal && (
        <BudgetVerwaltung
          showFormDirectly={true}
          presetData={null}
          onClose={handleCloseAddPositionModal}
        />
      )}
    </div>
  );
};

export default CashflowPlanung;
